package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.kyd.util.Vector2

/**
 * A [SimpleShape] contains just one set of [points], such as [Line] and [Polygon].
 *
 * If shapes with holes or other compound shapes are implemented, these will NOT extend [SimpleShape].
 */
abstract class SimpleShape(initialPoints: Iterable<Vector2>) : Shape() {

    protected val mutablePoints = mutableListOf<Vector2>()

    val points: List<Vector2> get() = mutablePoints

    open operator fun get(index: Int): Vector2 = points[index]

    private var minX: Float = 0f
    private var minY: Float = 0f
    private var maxX: Float = 0f
    private var maxY: Float = 0f

    init {
        this.mutablePoints.addAll(initialPoints)
        updateCounterProperty.addListener {
            reCalculateBounds()
        }
        updateCounter ++
    }

    protected open fun reCalculateBounds() {
        minX = points.minOfOrNull { it.x } ?: 0f
        minY = points.minOfOrNull { it.y } ?: 0f
        maxX = points.maxOfOrNull { it.x } ?: 0f
        maxY = points.maxOfOrNull { it.y } ?: 0f
    }

    override fun movePoint(index: Int, newPoint: Vector2) {
        mutablePoints[index] = newPoint
        updateCounter ++
    }

    override fun deletePoint(index: Int) {
        if (this is Line && points.size < 3) return
        if (this is Polygon && points.size < 4) return

        mutablePoints.removeAt(index)
        updateCounter ++
    }

    override fun addPoint(index: Int) {
        if (points.size < 2) return

        val point = points[index]
        val nextPoint = if (this is Line && index == points.size-1) {
            point * 2f - points[index - 1]
        } else {
            get(index + 1)
        }
        val newPoint = (point + nextPoint) / 2f
        mutablePoints.add(index + 1, newPoint)
        updateCounter ++
    }

    override fun bounds() = Pair(Vector2(minX, minY), Vector2(maxX, maxY))

}
